import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, MessageCircle, Calendar } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { SERVICES_BY_SLUG, SERVICES, type Service } from "@/lib/services";
import { SITE } from "@/lib/site";

const BASE = "https://nsphysiotherapy.co.za";

export const Route = createFileRoute("/services/$slug")({
  beforeLoad: ({ params }) => {
    // Home physiotherapy is merged into the Physio@Home page
    if (params.slug === "home-physiotherapy") {
      throw redirect({ to: "/physio-at-home", statusCode: 301 });
    }
  },
  loader: ({ params }) => {
    const service = SERVICES_BY_SLUG[params.slug];
    if (!service) throw notFound();
    return { service };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: "Service not found" }, { name: "robots", content: "noindex" }] };
    }
    const s = loaderData.service;
    const url = `${BASE}/services/${params.slug}`;
    return {
      meta: [
        { title: s.metaTitle },
        { name: "description", content: s.metaDescription },
        { property: "og:title", content: s.metaTitle },
        { property: "og:description", content: s.metaDescription },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalTherapy",
            name: s.title,
            description: s.metaDescription,
            url,
            provider: { "@id": `${BASE}/#business` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: s.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbJsonLd([
            { name: "Home", url: `${BASE}/` },
            { name: "Services", url: `${BASE}/services` },
            { name: s.title, url },
          ])),
        },
      ],
    };
  },
  component: ServicePage,
  notFoundComponent: ServiceNotFound,
});

function ServiceNotFound() {
  return (
    <PageShell>
      <div className="container-page py-24 text-center">
        <h1 className="font-display text-3xl font-bold text-primary">Service not found</h1>
        <p className="mt-3 text-muted-foreground">The service you're looking for doesn't exist. Browse all our services below.</p>
        <Link to="/services" className="mt-6 btn-hero inline-flex h-11 items-center rounded-full px-6 font-semibold">View services</Link>
      </div>
    </PageShell>
  );
}

function ServicePage() {
  const { service: s } = Route.useLoaderData() as { service: Service };
  const related = s.related
    .map((slug) => SERVICES_BY_SLUG[slug])
    .filter((r): r is Service => Boolean(r));
  const wa = `https://wa.me/${SITE.whatsappIntl}?text=${encodeURIComponent(`Hi, I'd like to book a physio appointment for ${s.eyebrow}.`)}`;

  return (
    <PageShell>
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Services", to: "/services" }, { label: s.eyebrow }]} />

      <section className="container-page py-10 md:py-14">
        <div className="max-w-3xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-primary/60">{s.eyebrow}</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold text-primary">{s.title}</h1>
          <p className="mt-5 text-foreground/80 md:text-lg">{s.intro}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/booking" className="btn-hero inline-flex h-11 items-center gap-2 rounded-full px-6 font-semibold">
              <Calendar className="h-4 w-4" /> Book online
            </Link>
            <a href={wa} target="_blank" rel="noreferrer" className="inline-flex h-11 items-center gap-2 rounded-full border border-border px-6 font-semibold text-primary hover:bg-accent">
              <MessageCircle className="h-4 w-4" /> WhatsApp us
            </a>
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container-page py-12 grid gap-8 md:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-bold text-primary">Who we help</h2>
            <ul className="mt-4 space-y-2 text-foreground/80">
              {s.whoWeHelp.map((w) => (
                <li key={w} className="flex gap-2"><CheckCircle2 className="h-5 w-5 shrink-0 text-primary" /> <span>{w}</span></li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold text-primary">What you'll gain</h2>
            <ul className="mt-4 space-y-2 text-foreground/80">
              {s.benefits.map((b) => (
                <li key={b} className="flex gap-2"><CheckCircle2 className="h-5 w-5 shrink-0" style={{ color: "var(--brand-teal)" }} /> <span>{b}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container-page py-12">
          <h2 className="font-display text-2xl font-bold text-primary">Our treatment approach</h2>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {s.approach.map((a) => (
              <div key={a.heading} className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-display text-lg font-bold text-primary">{a.heading}</h3>
                <p className="mt-2 text-sm text-foreground/75">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border">
        <div className="container-page py-12 max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-primary">Frequently asked questions</h2>
          <div className="mt-6 divide-y divide-border rounded-2xl border border-border bg-card">
            {s.faqs.map((f) => (
              <details key={f.q} className="group p-5">
                <summary className="cursor-pointer list-none font-semibold text-primary flex items-center justify-between">
                  {f.q}
                  <span className="text-primary/50 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-foreground/75">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="border-t border-border">
          <div className="container-page py-12">
            <h2 className="font-display text-2xl font-bold text-primary">Related services</h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to="/services/$slug" params={{ slug: r.slug }} className="group block rounded-2xl border border-border bg-card p-5 hover:border-primary/40">
                  <div className="text-xs font-semibold uppercase tracking-widest text-primary/60">{r.eyebrow}</div>
                  <div className="mt-1 font-display font-bold text-primary">{r.title}</div>
                  <span className="mt-2 inline-flex items-center gap-1 text-sm text-primary group-hover:underline">Learn more <ArrowRight className="h-4 w-4" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="border-t border-border bg-secondary/40">
        <div className="container-page py-12 text-center">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-primary">Ready to feel better?</h2>
          <p className="mt-3 text-foreground/75">Same day appointments available. No referral required.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/booking" className="btn-hero inline-flex h-11 items-center gap-2 rounded-full px-6 font-semibold">
              <Calendar className="h-4 w-4" /> Book online
            </Link>
            <a href={wa} target="_blank" rel="noreferrer" className="inline-flex h-11 items-center gap-2 rounded-full border border-border px-6 font-semibold text-primary bg-background hover:bg-accent">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

// Preload known slugs for static/dynamic imports
export const _knownSlugs = SERVICES.map((s) => s.slug);
